import React from 'react';
import { motion } from 'framer-motion';

// ═══════════════════════════════════════════════════════════════
// DICE DISPLAY COMPONENT
// ═══════════════════════════════════════════════════════════════

interface DieProps {
  value: number | null;
  size?: 'normal' | 'large';
  isTide?: boolean;
  isHidden?: boolean;
  isRolling?: boolean;
}

export const Die: React.FC<DieProps> = ({
  value,
  size = 'normal',
  isTide = false,
  isHidden = false,
  isRolling = false,
}) => {
  const sizeClass = size === 'large' ? 'bs-die-large' : '';
  const tideClass = isTide ? 'bs-die-tide' : '';
  const hiddenClass = isHidden || value === null ? 'bs-die-hidden' : '';
  const rollingClass = isRolling ? 'bs-die-rolling' : '';

  // Get die face character
  const getDieFace = (val: number): string => {
    const faces = ['', '⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];
    return faces[val] || '?';
  };

  return (
    <motion.div
      className={`bs-die ${sizeClass} ${tideClass} ${hiddenClass} ${rollingClass}`}
      initial={isRolling ? { rotate: 0 } : false}
      animate={
        isRolling
          ? { rotate: [0, 360, 720, 1080] }
          : { rotate: 0 }
      }
      transition={{ duration: 1.5, ease: 'easeOut' }}
    >
      {!isHidden && value !== null ? getDieFace(value) : null}
    </motion.div>
  );
};

interface DiceDisplayProps {
  tideDie: number;
  scoundrelDice: number[];
  showScoundrel: boolean;
  isRolling?: boolean;
  cumulativeTotal?: number; // Total of ALL rolls this voyage (for truthfulness comparison)
  currentHaul?: number;     // Coins already in ship from previous declarations
}

export const DiceDisplay: React.FC<DiceDisplayProps> = ({
  tideDie,
  scoundrelDice,
  showScoundrel,
  isRolling = false,
  cumulativeTotal,
  currentHaul = 0,
}) => {
  const thisRollTotal = tideDie + scoundrelDice.reduce((a, b) => a + b, 0);
  const hasPreviousRolls = currentHaul > 0;

  return (
    <div className="bs-dice-area">
      {/* Tide Die - Always Visible */}
      <div className="bs-dice-section">
        <span className="bs-dice-label">Tide Die (Public)</span>
        <Die
          value={tideDie}
          size="large"
          isTide
          isRolling={isRolling}
        />
      </div>

      {/* Scoundrel Dice - Hidden from others */}
      <div className="bs-dice-section">
        <span className="bs-dice-label">
          Scoundrel Dice {!showScoundrel && '(Hidden)'}
        </span>
        <div className="bs-dice-row">
          {scoundrelDice.map((value, index) => (
            <Die
              key={index}
              value={value}
              isHidden={!showScoundrel}
              isRolling={isRolling}
            />
          ))}
        </div>
      </div>

      {/* Total */}
      {showScoundrel && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: isRolling ? 1.5 : 0 }}
          style={{ textAlign: 'center' }}
        >
          {hasPreviousRolls ? (
            <>
              <span className="bs-dice-label">This Roll</span>
              <div style={{
                fontFamily: 'var(--bs-font-heading)',
                fontSize: '1.5rem',
                color: 'var(--bs-text-secondary)',
                marginBottom: '0.5rem',
              }}>
                +{thisRollTotal}
              </div>
              <span className="bs-dice-label">Your True Total (All Rolls)</span>
              <div style={{
                fontFamily: 'var(--bs-font-heading)',
                fontSize: '2.5rem',
                color: 'var(--bs-gold-bright)',
              }}>
                {cumulativeTotal ?? thisRollTotal}
              </div>
              <span className="bs-dice-label" style={{ marginTop: '0.5rem', display: 'block' }}>
                Coins Claimed So Far
              </span>
              <div style={{
                fontFamily: 'var(--bs-font-heading)',
                fontSize: '1.2rem',
                color: 'var(--bs-sea-teal)',
              }}>
                {currentHaul}
              </div>
            </>
          ) : (
            <>
              <span className="bs-dice-label">True Total</span>
              <div style={{
                fontFamily: 'var(--bs-font-heading)',
                fontSize: '2.5rem',
                color: 'var(--bs-gold-bright)',
              }}>
                {thisRollTotal}
              </div>
            </>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default DiceDisplay;
